import React, { useState } from 'react';
import { CompleteKundali, GunMilanResult, Language } from '../types/kundali';
import { calculateGunMilan } from '../utils/gunMilan';
import { deduplicateKundalis } from '../utils/kundaliDedupe';
import { formatDOB } from '../utils/dateUtils';
import { Users, Heart, Award, CheckCircle, AlertTriangle, Sparkles, UserCheck } from 'lucide-react';
import { getZodiacName } from '../utils/language';

interface MatchingModuleProps {
  currentKundali: CompleteKundali | null;
  savedKundalis: CompleteKundali[];
  lang?: Language;
  onOpenBirthForm: () => void;
}

export const MatchingModule: React.FC<MatchingModuleProps> = ({
  currentKundali,
  savedKundalis,
  lang = 'en',
  onOpenBirthForm
}) => {
  const isHi = lang === 'hi';
  const uniqueSavedKundalis = deduplicateKundalis(savedKundalis);
  const [boyKundali, setBoyKundali] = useState<CompleteKundali | null>(null);
  const [girlKundali, setGirlKundali] = useState<CompleteKundali | null>(null);

  const [matchResult, setMatchResult] = useState<GunMilanResult | null>(null);

  const handleMatch = () => {
    if (boyKundali && girlKundali) {
      setMatchResult(calculateGunMilan(boyKundali, girlKundali));
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Couple Selector Card */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4 border-b border-astro-border pb-3">
          <Heart className="w-5 h-5 text-astro-primary" />
          <h3 className="font-bold text-astro-primary font-serif text-lg">
            {isHi ? 'अष्टकूट गुण मिलान (36 गुण वैवाहिक अनुकूलता)' : 'Ashtakoot Gun Milan (36 Gunas Marriage Compatibility)'}
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          {/* Boy Selection */}
          <div className="p-4 bg-astro-card rounded-xl border border-astro-border space-y-2">
            <span className="text-[10px] uppercase font-bold text-astro-primary tracking-wider">
              {isHi ? 'वर / लड़के की कुंडली' : 'Groom / Boy Chart'}
            </span>
            {boyKundali ? (
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-astro-text text-sm">{boyKundali.birthDetails.name}</h4>
                  <p className="text-xs text-astro-secondary-text">
                    {formatDOB(boyKundali.birthDetails.date)} • {getZodiacName(boyKundali.houses[0].sign, lang)} {isHi ? 'लग्न' : 'Lagna'}
                  </p>
                </div>
                <button
                  onClick={() => setBoyKundali(null)}
                  className="text-xs text-astro-secondary-text hover:text-astro-text underline"
                >
                  {isHi ? 'बदलें' : 'Change'}
                </button>
              </div>
            ) : (
              <div className="text-xs text-astro-secondary-text space-y-2">
                <div>
                  <span>{isHi ? 'कोई वर कुंडली चयनित नहीं है। ' : 'No Boy chart selected. '}</span>
                  <button onClick={onOpenBirthForm} className="text-astro-primary underline font-semibold">
                    {isHi ? 'वर कुंडली बनाएं' : 'Create Boy Chart'}
                  </button>
                </div>
                {uniqueSavedKundalis.length > 0 && (
                  <select
                    onChange={(e) => {
                      const selected = uniqueSavedKundalis.find((k) => k.birthDetails.name === e.target.value);
                      if (selected) setBoyKundali(selected);
                    }}
                    defaultValue=""
                    className="w-full text-xs p-2 rounded-lg bg-white border border-astro-border text-astro-text"
                  >
                    <option value="" disabled>
                      {isHi ? `सहेजे गए में से चुनें (${uniqueSavedKundalis.length})` : `Select from Saved (${uniqueSavedKundalis.length})`}
                    </option>
                    {uniqueSavedKundalis.map((k, i) => (
                      <option key={i} value={k.birthDetails.name}>
                        {k.birthDetails.name} ({formatDOB(k.birthDetails.date)} - {k.birthDetails.placeName})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>

          {/* Girl Selection */}
          <div className="p-4 bg-astro-card rounded-xl border border-astro-border space-y-2">
            <span className="text-[10px] uppercase font-bold text-astro-accent tracking-wider">
              {isHi ? 'कन्या / लड़की की कुंडली' : 'Bride / Girl Chart'}
            </span>
            {girlKundali ? (
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-astro-text text-sm">{girlKundali.birthDetails.name}</h4>
                  <p className="text-xs text-astro-secondary-text">
                    {formatDOB(girlKundali.birthDetails.date)} • {getZodiacName(girlKundali.houses[0].sign, lang)} {isHi ? 'लग्न' : 'Lagna'}
                  </p>
                </div>
                <button
                  onClick={() => setGirlKundali(null)}
                  className="text-xs text-astro-secondary-text hover:text-astro-text underline"
                >
                  {isHi ? 'बदलें' : 'Change'}
                </button>
              </div>
            ) : (
              <div className="text-xs text-astro-secondary-text space-y-2">
                <div>
                  <span>{isHi ? 'कोई कन्या कुंडली चयनित नहीं है। ' : 'No Girl chart selected. '}</span>
                  <button onClick={onOpenBirthForm} className="text-astro-primary underline font-semibold">
                    {isHi ? 'कन्या कुंडली बनाएं' : 'Create Girl Chart'}
                  </button>
                </div>
                {uniqueSavedKundalis.length > 0 && (
                  <select
                    onChange={(e) => {
                      const selected = uniqueSavedKundalis.find((k) => k.birthDetails.name === e.target.value);
                      if (selected) setGirlKundali(selected);
                    }}
                    defaultValue=""
                    className="w-full text-xs p-2 rounded-lg bg-white border border-astro-border text-astro-text"
                  >
                    <option value="" disabled>
                      {isHi ? `सहेजे गए में से चुनें (${uniqueSavedKundalis.length})` : `Select from Saved (${uniqueSavedKundalis.length})`}
                    </option>
                    {uniqueSavedKundalis.map((k, i) => (
                      <option key={i} value={k.birthDetails.name}>
                        {k.birthDetails.name} ({formatDOB(k.birthDetails.date)} - {k.birthDetails.placeName})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Calculate Button */}
        <div className="mt-5 flex justify-center">
          <button
            onClick={handleMatch}
            disabled={!boyKundali || !girlKundali}
            className="px-8 py-3 rounded-xl bg-astro-primary hover:bg-astro-primary/90 text-white font-bold text-sm shadow-md disabled:opacity-50 transition-all transform hover:scale-[1.02]"
          >
            {isHi ? 'अष्टकूट 36 गुण मिलान करें' : 'Calculate Ashtakoot Gun Milan (36 Gunas)'}
          </button>
        </div>
      </div>

      {/* Match Results Display */}
      {matchResult && (
        <div className="space-y-6 animate-fade-in">
          {/* Main Score & Verdict Card */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              {/* Score Circle */}
              <div className="relative w-28 h-28 rounded-full bg-astro-card border-4 border-astro-primary flex flex-col items-center justify-center shadow-inner shrink-0">
                <span className="text-3xl font-extrabold text-astro-primary font-mono">
                  {matchResult.totalScore}
                </span>
                <span className="text-[10px] text-astro-secondary-text font-bold uppercase">{isHi ? '36 में से' : 'out of 36'}</span>
              </div>

              <div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold border ${
                    matchResult.totalScore >= 21
                      ? 'bg-astro-primary/10 text-astro-primary border-astro-primary/30'
                      : 'bg-rose-100 text-rose-700 border-rose-300'
                  }`}
                >
                  {isHi ? (matchResult.totalScore >= 28 ? 'उत्कृष्ट मिलान' : matchResult.totalScore >= 18 ? 'स्वीकार्य मिलान' : 'दोषयुक्त / कम गुण') : matchResult.verdict}
                </span>
                <h3 className="text-xl font-bold text-astro-text font-serif mt-2">
                  {matchResult.boyName} & {matchResult.girlName}
                </h3>
                <p className="text-xs text-astro-text leading-relaxed max-w-xl mt-1">
                  {matchResult.summary}
                </p>
              </div>
            </div>

            {/* Manglik & Ashtakvarga Compatibility Badges */}
            <div className="flex flex-col gap-3 max-w-xs w-full">
              <div className="p-3.5 bg-astro-card rounded-xl border border-astro-border text-xs text-astro-text space-y-1">
                <span className="text-[10px] uppercase font-bold text-astro-primary block">{isHi ? 'मांगलिक अनुकूलता' : 'Manglik Compatibility'}</span>
                <p className="font-semibold text-astro-text">{matchResult.manglikMatch.note}</p>
              </div>

              {matchResult.ashtakvargaMatch && (
                <div className="p-3.5 bg-astro-card rounded-xl border border-astro-border text-xs text-astro-text space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase font-bold text-astro-primary">{isHi ? 'अष्टकवर्ग सामंजस्य' : 'Ashtakvarga Harmony'}</span>
                    <span className="text-xs font-extrabold text-astro-primary font-mono">
                      {matchResult.ashtakvargaMatch.compatibilityPercentage}%
                    </span>
                  </div>
                  <p className="text-[11px] text-astro-secondary-text">
                    {matchResult.ashtakvargaMatch.summary}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Ashtakvarga Compatibility Detail Box */}
          {matchResult.ashtakvargaMatch && (
            <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3 border-b border-astro-border pb-2">
                <Award className="w-5 h-5 text-astro-primary" />
                <h3 className="font-bold text-astro-primary font-serif text-base">
                  {isHi ? 'अष्टकवर्ग बिंदु अनुकूलता विश्लेषण' : 'Ashtakvarga Bindu Compatibility Analysis'}
                </h3>
              </div>
              <p className="text-xs text-astro-secondary-text mb-4">
                {isHi 
                  ? 'अष्टकवर्ग से भावों एवं गोचरों की वास्तविक शक्ति का आकलन होता है। जीवनसाथी के सर्वाष्टकवर्ग में 28+ बिंदु वैवाहिक सहयोग और सुख प्रदान करते हैं।'
                  : 'Ashtakvarga evaluates the physical strength of planetary transits and houses. A score of 28+ Bindus in a partner\'s Sarvashtakvarga (SAV) indicates smooth life support, prosperity, and emotional cushion.'}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
                  <span className="text-astro-secondary-text text-[10px] uppercase block font-semibold">{isHi ? 'कन्या SAV में वर चंद्र' : 'Groom Moon in Bride SAV'}</span>
                  <span className="font-bold text-astro-primary text-sm">
                    {matchResult.ashtakvargaMatch.boyMoonSignBindusInGirlSAV} {isHi ? 'बिंदु' : 'Bindus'}
                  </span>
                  <span className="text-[10px] text-astro-secondary-text block mt-0.5">
                    {matchResult.ashtakvargaMatch.boyMoonSignBindusInGirlSAV >= 28 ? (isHi ? '✓ उत्तम सहयोग' : '✓ Strong Support') : (isHi ? '• सामान्य' : '• Average Support')}
                  </span>
                </div>

                <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
                  <span className="text-astro-secondary-text text-[10px] uppercase block font-semibold">{isHi ? 'वर SAV में कन्या चंद्र' : 'Bride Moon in Groom SAV'}</span>
                  <span className="font-bold text-astro-primary text-sm">
                    {matchResult.ashtakvargaMatch.girlMoonSignBindusInBoySAV} {isHi ? 'बिंदु' : 'Bindus'}
                  </span>
                  <span className="text-[10px] text-astro-secondary-text block mt-0.5">
                    {matchResult.ashtakvargaMatch.girlMoonSignBindusInBoySAV >= 28 ? (isHi ? '✓ उत्तम सहयोग' : '✓ Strong Support') : (isHi ? '• सामान्य' : '• Average Support')}
                  </span>
                </div>

                <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
                  <span className="text-astro-secondary-text text-[10px] uppercase block font-semibold">{isHi ? 'वर सप्तम भाव (विवाह)' : 'Groom 7th House (Marriage)'}</span>
                  <span className="font-bold text-astro-text text-sm">
                    {matchResult.ashtakvargaMatch.boy7thHouseBindus} {isHi ? 'बिंदु' : 'Bindus'}
                  </span>
                  <span className="text-[10px] text-astro-secondary-text block mt-0.5">
                    {matchResult.ashtakvargaMatch.boy7thHouseBindus >= 28 ? (isHi ? '✓ सशक्त भाव' : '✓ Healthy House') : (isHi ? '• ध्यान योग्य' : '• Remedial Care')}
                  </span>
                </div>

                <div className="p-3 bg-astro-card rounded-xl border border-astro-border">
                  <span className="text-astro-secondary-text text-[10px] uppercase block font-semibold">{isHi ? 'कन्या सप्तम भाव (विवाह)' : 'Bride 7th House (Marriage)'}</span>
                  <span className="font-bold text-astro-text text-sm">
                    {matchResult.ashtakvargaMatch.girl7thHouseBindus} {isHi ? 'बिंदु' : 'Bindus'}
                  </span>
                  <span className="text-[10px] text-astro-secondary-text block mt-0.5">
                    {matchResult.ashtakvargaMatch.girl7thHouseBindus >= 28 ? (isHi ? '✓ सशक्त भाव' : '✓ Healthy House') : (isHi ? '• ध्यान योग्य' : '• Remedial Care')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 8 Koots Breakdown Table */}
          <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between mb-4 border-b border-astro-border pb-3">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-astro-primary" />
                <h3 className="font-bold text-astro-primary font-serif text-lg">{isHi ? 'अष्टकूट 8 घटक विवरण' : 'Ashtakoot 8-Factor Breakdown'}</h3>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-astro-border text-astro-secondary-text uppercase text-[10px] tracking-wider font-semibold bg-astro-card">
                    <th className="py-3 px-3">{isHi ? 'कूट (गुण)' : 'Guna (Koot)'}</th>
                    <th className="py-3 px-3">{isHi ? 'वर गुणधर्म' : 'Groom Attribute'}</th>
                    <th className="py-3 px-3">{isHi ? 'कन्या गुणधर्म' : 'Bride Attribute'}</th>
                    <th className="py-3 px-3">{isHi ? 'प्राप्त अंक' : 'Score'}</th>
                    <th className="py-3 px-3">{isHi ? 'स्थिति' : 'Status'}</th>
                    <th className="py-3 px-3">{isHi ? 'वर्णन' : 'Description'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-astro-border text-astro-text">
                  {matchResult.gunas.map((g) => (
                    <tr key={g.name} className="hover:bg-astro-card transition-colors">
                      <td className="py-3 px-3 font-bold text-astro-primary">{g.name}</td>
                      <td className="py-3 px-3 text-astro-text">{g.boyAttribute}</td>
                      <td className="py-3 px-3 text-astro-text">{g.girlAttribute}</td>
                      <td className="py-3 px-3 font-mono font-bold text-astro-text">
                        {g.obtainedPoints} / {g.maxPoints}
                      </td>
                      <td className="py-3 px-3">
                        {g.status === 'Excellent' && (
                          <span className="text-astro-primary font-semibold text-[11px]">{isHi ? 'उत्कृष्ट' : 'Excellent'}</span>
                        )}
                        {g.status === 'Good' && (
                          <span className="text-[#6F9690] font-semibold text-[11px]">{isHi ? 'उत्तम' : 'Good'}</span>
                        )}
                        {g.status === 'Average' && (
                          <span className="text-astro-accent font-semibold text-[11px]">{isHi ? 'सामान्य' : 'Average'}</span>
                        )}
                        {g.status === 'Poor' && (
                          <span className="text-rose-600 font-semibold text-[11px]">{isHi ? 'कमजोर' : 'Poor'}</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-astro-secondary-text text-[11px] max-w-xs">{g.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
